import {useRouter} from 'next/router';
import {ReactNode, useCallback, useEffect, useState} from 'react';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {useWrapRef} from '../../../../../../common/useWrapRef.hook';
import {Loading} from '../../../../../../design-system/atoms/Loading/Loading';
import {PagewebErrorType} from '../../../../../../errors/PagewebErrorType.enum';
import {SerializedPagewebError} from '../../../../../../errors/SerializedPagewebError.interface';
import {Maybe, QueryFromPage} from '../../../../../../graphql/generated/types';
import {useBuyTicketFlow} from '../../../../../buy/ticket/hook/useBuyTicketFlow';
import {BuyTicketFlowContext} from '../../../../../buy/ticket/type';
import {ViewerEntryErrorHandlerHeadless} from './ViewerEntryErrorHandlerHeadless';

export function ViewerEntryCheckerHeadless({
  seriesId,
  productId,
  children,
}: {
  seriesId: number;
  productId: number;
  children: ReactNode;
}) {
  const router = useRouter();
  const [viewerEntryStatus, setViewerEntryStatus] = useState<
    Record<number, 'loading' | 'success' | 'error'>
  >({});
  const [error, setError] = useState<SerializedPagewebError | undefined>(
    undefined
  );
  const [ticketProcessCalled, setTicketProcessCalled] = useState(false);
  const ticketFlowInProgress = useTypedSelector(
    state => state.viewerInfo.ticketFlowInProgress
  );

  const {startTicketProcess: _startTicketProcess} = useBuyTicketFlow({
    onInit: () =>
      setViewerEntryStatus(status => ({...status, [productId]: 'loading'})),
    onSuccess: (context: BuyTicketFlowContext) => {
      if (context.currentProductId === context.ticketProcessParam?.productId) {
        setViewerEntryStatus(status => ({...status, [productId]: 'success'}));
      } else {
        router.push(
          `/content/${context.ticketProcessParam?.seriesId}/viewer/${context.ticketProcessParam?.productId}`
        );
      }
    },
    onError: (error: Maybe<SerializedPagewebError>) => {
      if (error) {
        // 이용권 선택 팝업에서 취소한 경우 현재 무료 회차를 그대로 보여줍니다.
        if (
          error.type === PagewebErrorType.NeedToBuyTicketError &&
          error.extra?.source === 'ticketChoicePopupCancel'
        ) {
          setViewerEntryStatus(status => ({...status, [productId]: 'success'}));
          return;
        }
        setError(error);
        setViewerEntryStatus(status => ({...status, [productId]: 'error'}));
      } else {
        // onError 핸들러가 호출됐으나 throw 할 에러 객체가 존재하지 않는 경우는 별도 처리하지 않습니다.
        setViewerEntryStatus(status => ({...status, [productId]: 'success'}));
      }
    },
  });

  const startTicketProcess = useCallback(() => {
    setViewerEntryStatus(status => ({...status, [productId]: 'loading'}));
    setError(undefined);
    _startTicketProcess({
      seriesId,
      currentProductId: productId,
      targetProductId: productId,
      nonstopWatching: false,
      queryFrom: QueryFromPage.ViewerEntry,
      pickExactly: false,
    });
  }, [seriesId, productId, _startTicketProcess]);

  const startTicketProcessRef = useWrapRef(startTicketProcess);

  useEffect(() => {
    // 뷰어 진입과 동시에 이용권 프로세스를 시작합니다.
    // 아직 이전 티켓 프로세스가 완료되지 않았다면 완료될 때 까지 대기합니다.
    if (!ticketProcessCalled && !ticketFlowInProgress) {
      setTicketProcessCalled(true);
      startTicketProcessRef.current?.();
    }
  }, [
    startTicketProcessRef,
    seriesId,
    productId,
    ticketProcessCalled,
    ticketFlowInProgress,
  ]);

  useEffect(() => {
    // 뷰어 이탈 및 회차 변경 시 상태 초기화합니다.
    return () => {
      setTicketProcessCalled(false);
      setError(undefined);
      setViewerEntryStatus(status => ({...status, [productId]: 'loading'}));
    };
  }, [seriesId, productId]);

  const currentViewerEntryStatus = viewerEntryStatus[productId];

  return (
    <>
      {currentViewerEntryStatus === 'loading' && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
      {currentViewerEntryStatus === 'success' && <>{children}</>}
      {currentViewerEntryStatus === 'error' && (
        <ViewerEntryErrorHandlerHeadless
          seriesId={seriesId}
          productId={productId}
          error={error}
          retry={startTicketProcess}
        />
      )}
    </>
  );
}
